import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import LobbyChat from './LobbyChat';
import { syncServerTime, msUntil, formatCountdown } from '../../common/clock';

/**
 * The waiting room's chat, on its own page.
 *
 * Carries the countdown and the list of who is present, so a player waiting
 * here does not have to go back to the contest page to see how long is left.
 */
export default class ChatPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);

    this.bodyClass = 'App--happyQuiz';

    const slug = String(m.route.param('slug') || '');
    this.contestId = /^[1-9]\d*(?:-|$)/.test(slug) ? parseInt(slug, 10) : null;

    this.contest = null;
    this.loading = true;
    this.failed = false;

    this.load();

    // Redraws the countdown without re-fetching.
    this.ticker = setInterval(() => {
      if (!document.hidden) m.redraw();
    }, 1000);

    // Keeps the roster and the phase fresh while someone sits here.
    this.poller = setInterval(() => {
      if (!document.hidden) this.load();
    }, 30000);
  }

  onremove(vnode) {
    super.onremove(vnode);

    clearInterval(this.ticker);
    clearInterval(this.poller);
  }

  load() {
    if (!this.contestId) {
      this.loading = false;
      this.failed = true;
      return Promise.resolve();
    }

    return app
      .request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/happy-quiz/contests/${this.contestId}`,
      })
      .then((response) => {
        this.contest = response.data;
        this.loading = false;

        syncServerTime(this.contest.state?.serverNow);
        m.redraw();
      })
      .catch(() => {
        this.loading = false;
        this.failed = true;
        m.redraw();
      });
  }

  view() {
    if (this.loading) {
      return (
        <div className="HappyQuiz">
          <LoadingIndicator />
        </div>
      );
    }

    if (this.failed || !this.contest) {
      return (
        <div className="HappyQuiz">
          <p className="HappyQuiz-rule">{app.translator.trans('happydigital-quiz.forum.error.generic')}</p>
        </div>
      );
    }

    const contest = this.contest;

    return (
      <div className="HappyQuiz HappyQuizChatPage">
        {/* One bar: back, who is here, and how long is left. */}
        <header className="HappyQuizChatPage-head">
          <Link
            className="HappyQuizChatPage-back"
            href={app.route('happyQuiz.show', { slug: contest.slug })}
            aria-label={app.translator.trans('happydigital-quiz.forum.chat.back')}
          >
            <i className="fas fa-chevron-left" aria-hidden="true" />
          </Link>

          <div className="HappyQuizChatPage-title">
            <h1>{contest.title}</h1>
            {this.roster(contest)}
          </div>

          {this.countdown(contest)}
        </header>

        {this.champion(contest)}

        <LobbyChat contestId={contest.id} />
      </div>
    );
  }

  /**
   * Time until the contest starts. Once it has, the chat is closed anyway, so
   * the counter gives way to a plain notice.
   */
  countdown(contest) {
    const remaining = msUntil(contest.state?.startsAt);

    if (remaining <= 0) {
      return <span className="HappyQuizChatPage-started">{app.translator.trans('happydigital-quiz.forum.chat.started')}</span>;
    }

    return (
      <span className="HappyQuizChatPage-countdown" role="timer" aria-live="off">
        <small>{app.translator.trans('happydigital-quiz.forum.chat.starts_in')}</small>
        <b>{formatCountdown(remaining)}</b>
      </span>
    );
  }

  /**
   * The previous contest's winner: the mark to beat, stated as a person.
   *
   * Shown whether or not they are in the room — the point is the target, not
   * who is present.
   */
  champion(contest) {
    const winner = contest.previousWinner;

    if (!winner) return null;

    const face = [
      <span className="HappyQuizChatPage-championAvatar">
        {winner.avatarUrl ? (
          <img src={winner.avatarUrl} alt="" />
        ) : (
          (winner.username || '?').charAt(0).toLocaleUpperCase()
        )}
        <span className="HappyQuizChatPage-crown" aria-hidden="true">
          <i className="fas fa-crown" />
        </span>
      </span>,
      <span className="HappyQuizChatPage-championText">
        <small>{app.translator.trans('happydigital-quiz.forum.chat.champion_label')}</small>
        <b>{winner.username}</b>
      </span>,
    ];

    return (
      <div className="HappyQuizChatPage-champion">
        {winner.userSlug ? (
          <Link
            className="HappyQuizChatPage-championLink"
            href={app.route('user', { username: winner.userSlug })}
          >
            {face}
          </Link>
        ) : (
          <span className="HappyQuizChatPage-championLink">{face}</span>
        )}

        <span className="HappyQuizChatPage-championScore">
          {app.translator.trans('happydigital-quiz.forum.results.correct_count', {
            count: winner.correctCount,
          })}
        </span>
      </div>
    );
  }

  /**
   * Everyone in the room, including those who have not written anything.
   */
  roster(contest) {
    const participants = contest.participants || [];

    if (!participants.length) return null;

    const total = contest.participantCount || participants.length;

    return (
      <span className="HappyQuizChatPage-roster">
        <span className="HappyQuizChatPage-faces">
          {/* A handful of faces is enough as a subtitle; the count carries the
              rest. */}
          {participants.slice(0, 5).map((person) => (
            <span
              className={'HappyQuizChatPage-face' + (person.isMe ? ' is-me' : '')}
              title={person.username}
            >
              {person.avatarUrl ? (
                <img src={person.avatarUrl} alt="" />
              ) : (
                (person.username || '?').charAt(0).toLocaleUpperCase()
              )}
            </span>
          ))}
        </span>

        <span className="HappyQuizChatPage-rosterCount">
          {app.translator.trans('happydigital-quiz.forum.chat.present', { count: total })}
        </span>
      </span>
    );
  }
}
